import { Skeleton } from "@/components/_components/skeleton";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import * as api from "@/lib/api/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useEffect } from "react";
import { Shield, Users, BarChart3, Edit, Key } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { usePageHeader } from "@/contexts/page-header-context";
import { Badge } from "@/components/_components/badge";
import type { RoleDto } from "@/lib/api/types";
import CreateRoleDialog from "./_components/CreateRoleDialog.tsx";
import DeleteRoleDialog from "./_components/DeleteRoleDialog.tsx";
import ManageAuthoritiesDialog from "./_components/ManageAuthoritiesDialog.tsx";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { ErrorDisplay } from "@/components/_components/error-display";
import { useErrorHandler } from "@/hooks/useErrorHandler";

const RoleManagementPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_ROLE_MGMT_VIEW">
      <RoleManagementContent />
    </PageAccessGuard>
  );
};

const RoleManagementContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getAllRoles();
      setRoles(response.data || []);
    } catch (err: unknown) {
      setError(err);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: {
        count: roles.length,
        label: 'role',
        icon: <BarChart3 className="h-4 w-4" />
      },
      actions: (
        <CreateRoleDialog onSuccess={fetchRoles} />
      ),
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
  }, [roles.length, setHeaderData]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <ErrorDisplay error={error} onRetry={fetchRoles} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {roles.length === 0 ? (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Roles Configured</h3>
                  <p className="text-muted-foreground mb-4">
                    Start by creating your first role to manage user permissions.
                  </p>
                  <CreateRoleDialog onSuccess={fetchRoles} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-border/50 bg-card backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">
                      Role Management
                    </h2>
                    <Badge variant="secondary" className="text-xs font-medium">
                      {roles.length} {roles.length === 1 ? 'role' : 'roles'}
                    </Badge>
                  </div>
                </div>

                <div className="w-full">
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                        <TableRow>
                          <TableHead className="font-semibold">Role Name</TableHead>
                          <TableHead className="font-semibold">Updated By</TableHead>
                          <TableHead className="font-semibold">Updated At</TableHead>
                          <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {roles.map((role) => (
                          <TableRow 
                            key={role.id}
                            className="transition-colors hover:bg-muted/50 cursor-pointer"
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                {role.name}
                              </div>
                            </TableCell>
                            <TableCell>{role.updatedBy || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {role.updatedAt ? formatDateTime(role.updatedAt) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-center">
                                <ManageAuthoritiesDialog 
                                  role={role} 
                                  onSuccess={fetchRoles}
                                  trigger={
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600 transition-colors" 
                                      title="Manage Authorities"
                                    >
                                      <Key className="h-4 w-4" />
                                    </Button>
                                  }
                                />
                                <CreateRoleDialog 
                                  role={role} 
                                  onSuccess={fetchRoles}
                                  trigger={
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors" 
                                      title="Edit Role"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  }
                                />
                                <DeleteRoleDialog 
                                  role={role} 
                                  onSuccess={fetchRoles}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagementPage;